import {
  ArrowRightOutlined,
  BarChartOutlined,
  BookOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CopyOutlined,
  CustomerServiceOutlined,
  DownOutlined,
  GlobalOutlined,
  LockOutlined,
  MobileOutlined,
  PhoneOutlined,
  SignalFilled,
  ToolOutlined,
} from "@ant-design/icons";
import { Spin } from "antd";
import dayjs from "dayjs";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import {
  useGetEsimStatusQuery,
  useGetEsimUsageQuery,
  useGetRedemptionQuery,
  useGetTgtOrderQuery,
  useGetTgtUsageQuery,
} from "../api/apiSlice";
import CountryListModal from "../components/CountryListModal/CountryListModal";
import useCOperators from "../components/CountryListModal/useCountryOperators";
import exampleSimplyQr from "../assets/example/example-simply-qr.png";

const INSTALLATION_GUIDE_URL = "https://www.sim-ply.io/installation-guide";
const BUY_MORE_DATA_URL = "https://sim-ply.io/";
const BYTES_PER_GB = 1024 * 1024 * 1024;
const MB_PER_GB = 1000;

type PageParams = {
  redemptionCode?: string;
  simIdx?: string;
};

type StatusVariant = "live-usage" | "live-status" | "third-party";
type PanelName = "plan" | "usage" | "balance" | "installation";

type UsageSummary = {
  usedGb: number;
  totalGb: number;
  remainingGb: number;
};

const LifeBuoyIcon = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
    <path d="m5.64 5.64 3.53 3.53m5.66 5.66 3.53 3.53m0-12.72-3.53 3.53m-5.66 5.66-3.53 3.53" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const SimCardIcon = () => (
  <svg viewBox="5 1 15 22" width="1.5em" height="1.5em" fill="none" aria-hidden="true">
    <path d="M7 3h7l4 4v14H7z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <rect x="9.5" y="11" width="6" height="7" rx="1" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12.5 11v7m-3-3.5h6" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const SecureShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 2.5 20 6v5.5c0 5.1-3.4 8.7-8 10-4.6-1.3-8-4.9-8-10V6z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M12 8v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const PageRoot = styled.main`
  min-height: 100vh;
  overflow-x: clip;
  color: #171717;
  background: #fbfaf8;
  font-family: "Bio Sans Regular", Arial, sans-serif;
  text-transform: none;

  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

const Header = styled.header`
  padding: clamp(28px, 8vw, 44px) 0 clamp(24px, 7vw, 38px);
  color: #ffffff;
  background: linear-gradient(135deg, #ff5538, #ff3e28);
`;

const MobileCanvas = styled.div`
  width: min(calc(100% - 24px), 600px);
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: flex-end;
  gap: clamp(8px, 2.5vw, 14px);

  > div {
    min-width: 0;
  }
`;

const HeaderLogo = styled.img`
  display: block;
  width: clamp(150px, 47vw, 250px);
  height: auto;
`;

const ReadyText = styled.p`
  margin: clamp(22px, 6vw, 34px) 0 0 15px;
  font-size: clamp(18px, 5vw, 25px);
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
`;

const HeaderStatus = styled.span`
  min-width: 0;
  max-width: clamp(112px, 34vw, 160px);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  margin-bottom: 1px;
  padding: 8px 10px;
  border-radius: 999px;
  color: #8edb51;
  background: #222222;
  font-size: clamp(12px, 3.3vw, 16px);
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  line-height: 1.15;
  text-align: center;
  overflow-wrap: anywhere;

  &::before {
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: #79d147;
    content: "";
  }
`;

const Content = styled(MobileCanvas)`
  padding: 16px 0 34px;
`;

const NavigationSlot = styled.div`
  margin-bottom: 14px;
`;

const Card = styled.section`
  overflow: hidden;
  border: 1px solid #efeeeb;
  border-radius: clamp(17px, 4.8vw, 24px);
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(43, 33, 22, 0.07);
`;

const HeroCard = styled(Card)`
  padding: clamp(18px, 5vw, 30px);
`;

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(12px, 4vw, 20px);

  > div:last-child {
    min-width: 0;
  }
`;

const ProductBadge = styled.div`
  width: clamp(60px, 18vw, 82px);
  height: clamp(60px, 18vw, 82px);
  display: grid;
  place-items: center;
  flex: none;
  overflow: hidden;
  border-radius: 50%;
  color: #ffd94b;
  background: #1248a6;
  font-size: clamp(27px, 8vw, 40px);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProductName = styled.h1`
  margin: 0 0 4px;
  font-family: "Diazo Cond Bold", Arial, sans-serif;
  font-size: clamp(27px, 8vw, 43px);
  line-height: 1;
  overflow-wrap: anywhere;
`;

const Muted = styled.p`
  margin: 0;
  color: #6e7480;
  font-size: clamp(14px, 4vw, 20px);
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: clamp(9px, 2.8vw, 20px);
  margin-top: clamp(22px, 6vw, 32px);
`;

const HeroColumn = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const RightColumn = styled(HeroColumn)`
  align-items: stretch;
  padding-left: clamp(9px, 2.8vw, 20px);
  border-left: 1px solid #e7e5e1;
  text-align: left;
`;

const HeroIcon = styled.div`
  width: clamp(75px, 23vw, 126px);
  height: clamp(75px, 23vw, 126px);
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #fff4d7;
  font-size: clamp(31px, 9vw, 54px);
`;

const HeroTitle = styled.h2`
  margin: 14px 0 8px;
  font-family: "Diazo Cond Bold", Arial, sans-serif;
  font-size: clamp(18px, 5vw, 27px);
  line-height: 1.1;
`;

const InstructionText = styled.p`
  margin: 0;
  color: #59606c;
  font-size: clamp(12px, 3.4vw, 16px);
  line-height: 1.55;
  white-space: pre-line;
`;

const ShortCode = styled.div`
  width: 100%;
  margin-top: 18px;
  padding: clamp(11px, 3.4vw, 17px) 8px;
  overflow-wrap: anywhere;
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  background: #ffffff;
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(13px, 3.8vw, 19px);
`;

const Meter = styled.div<{ $usedPercentage: number }>`
  width: clamp(112px, 34vw, 195px);
  height: clamp(112px, 34vw, 195px);
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: ${({ $usedPercentage }) =>
    `conic-gradient(#36b54a 0 ${$usedPercentage}%, #e9eae8 ${$usedPercentage}% 100%)`};

  &::before {
    width: 82%;
    height: 82%;
    grid-area: 1 / 1;
    border-radius: 50%;
    background: #ffffff;
    content: "";
  }
`;

const MeterValue = styled.div`
  z-index: 1;
  grid-area: 1 / 1;
  font-family: "Bio Sans ExtraBold", Arial, sans-serif;
  font-size: clamp(19px, 5.5vw, 31px);
  line-height: 1.05;

  span {
    display: block;
    margin-top: 6px;
    color: #6e7480;
    font-family: "Bio Sans Regular", Arial, sans-serif;
    font-size: clamp(11px, 3vw, 15px);
  }
`;

const UsageCaption = styled.p`
  margin: 15px 0 0;
  color: #6e7480;
  font-size: clamp(12px, 3.4vw, 17px);
  line-height: 1.45;

  strong {
    color: #171717;
  }
`;

const StatusItem = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: clamp(9px, 2.8vw, 14px);
  padding: 12px 0;
  border-bottom: 1px solid #e7e5e1;

  > div {
    min-width: 0;
  }
`;

const CircleIcon = styled.span<{ $success?: boolean; $warning?: boolean }>`
  width: clamp(38px, 11vw, 56px);
  height: clamp(38px, 11vw, 56px);
  display: grid;
  place-items: center;
  flex: none;
  border-radius: 50%;
  color: ${({ $success, $warning }) => ($success ? "#159b2b" : $warning ? "#ff4b31" : "#171717")};
  background: ${({ $success, $warning }) => ($success ? "#e9f8ea" : $warning ? "#fff1ed" : "#f1f1ee")};
  font-size: clamp(17px, 5vw, 25px);
`;

const HelpHeadingIcon = styled(CircleIcon)`
  svg {
    width: 80%;
    height: 80%;
  }
`;

const SavedPageIcon = styled(CircleIcon)`
  width: clamp(42px, 12vw, 58px);
  height: clamp(42px, 12vw, 58px);
  color: #000000;
  background: #ffc928;

  svg {
    width: 66%;
    height: 66%;
  }
`;

const StatusLabel = styled.span`
  display: block;
  margin-bottom: 3px;
  color: #6e7480;
  font-size: clamp(11px, 3.1vw, 15px);
`;

const StatusValue = styled.strong`
  max-width: 100%;
  display: inline-block;
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(13px, 3.7vw, 19px);
  line-height: 1.2;
  overflow-wrap: anywhere;
`;

const StatusChip = styled(StatusValue)<{ $active: boolean }>`
  padding: 4px 9px;
  border-radius: 6px;
  color: ${({ $active }) => ($active ? "#168f29" : "#8a5212")};
  background: ${({ $active }) => ($active ? "#e9f8ea" : "#fff2d8")};
`;

const ActionLink = styled.a<{ $outline?: boolean }>`
  width: 100%;
  min-width: 0;
  min-height: 42px;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  padding: 6px 8px;
  border: ${({ $outline }) => ($outline ? "2px solid #ff4b31" : "2px solid #ff4b31")};
  border-radius: 10px;
  color: ${({ $outline }) => ($outline ? "#171717" : "#ffffff")};
  background: ${({ $outline }) => ($outline ? "#ffffff" : "#ff4b31")};
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(11px, 3vw, 15px);
  line-height: 1.15;
  text-align: center;
  text-decoration: none;

  .button-icon {
    justify-self: start;
  }

  .button-label {
    min-width: 0;
    justify-self: center;
    text-align: center;
  }

  .arrow {
    justify-self: end;
  }
`;

const ActionButton = styled.button<{ $outline?: boolean }>`
  width: 100%;
  min-width: 0;
  min-height: 42px;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) 18px;
  align-items: center;
  gap: 4px;
  margin-top: 10px;
  padding: 6px 8px;
  border: 2px solid #ff4b31;
  border-radius: 10px;
  color: ${({ $outline }) => ($outline ? "#171717" : "#ffffff")};
  background: ${({ $outline }) => ($outline ? "#ffffff" : "#ff4b31")};
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(11px, 3vw, 15px);
  line-height: 1.15;

  .button-icon {
    justify-self: start;
  }

  .button-label {
    min-width: 0;
    justify-self: center;
    text-align: center;
  }

  .arrow {
    justify-self: end;
  }
`;

const GenericValidity = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(13px, 4vw, 22px) 8px;
  border: 1px solid #eadcc8;
  border-radius: 14px;
  background: linear-gradient(145deg, #fff9ef, #f7f5f1);
  text-align: center;

  ${HeroIcon} {
    width: clamp(52px, 16vw, 88px);
    height: clamp(52px, 16vw, 88px);
    color: #5d5d5d;
    background: #eee3cf;
    font-size: clamp(22px, 6.5vw, 38px);
  }

  p {
    margin: 13px 0 0;
    color: #555d68;
    font-size: clamp(11px, 3vw, 14px);
    line-height: 1.5;
  }
`;

const Stack = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 16px;
`;

const AccordionButton = styled.button`
  width: 100%;
  min-height: clamp(70px, 19vw, 94px);
  display: flex;
  align-items: center;
  gap: clamp(11px, 3.5vw, 18px);
  padding: clamp(12px, 3.7vw, 18px);
  border: 0;
  color: #171717;
  background: #ffffff;
  text-align: left;
`;

const AccordionIcon = styled(CircleIcon)`
  color: #171717;
  background: #fff4d7;
`;

const UsageBreakdownIcon = styled(BarChartOutlined)`
  font-size: 1.2em;
`;

const AccordionCopy = styled.span`
  min-width: 0;
  flex: 1;
`;

const AccordionTitle = styled.strong`
  display: block;
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(15px, 4.2vw, 21px);
  line-height: 1.2;
`;

const AccordionHint = styled.span`
  display: block;
  margin-top: 4px;
  overflow: hidden;
  color: #6e7480;
  font-size: clamp(11px, 3.1vw, 15px);
  line-height: 1.35;
  text-overflow: ellipsis;
`;

const Chevron = styled(DownOutlined)<{ $open: boolean }>`
  flex: none;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;

const AccordionBody = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.22s ease;

  > div {
    min-height: 0;
    overflow: hidden;
  }
`;

const AccordionInner = styled.div`
  padding: clamp(14px, 4vw, 22px);
  border-top: 1px solid #e7e5e1;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(7px, 2.2vw, 12px);
`;

const DetailBox = styled.div`
  min-width: 0;
  padding: clamp(9px, 2.8vw, 15px);
  border: 1px solid #e7e5e1;
  border-radius: 11px;
  background: #fffdfa;

  span {
    display: block;
    margin-bottom: 5px;
    color: #6e7480;
    font-size: clamp(9px, 2.5vw, 12px);
  }

  strong {
    overflow-wrap: anywhere;
    font-size: clamp(11px, 3vw, 15px);
    line-height: 1.3;
  }
`;

const UsageList = styled.div`
  display: grid;
  grid-template-columns: minmax(64px, 0.8fr) minmax(0, 1fr) max-content;
  gap: 14px 9px;
  align-items: center;

  > section,
  > p {
    grid-column: 1 / -1;
  }
`;

const UsageRow = styled.div`
  display: contents;
  font-size: clamp(10px, 2.9vw, 14px);

  strong {
    white-space: nowrap;
    text-align: right;
  }
`;

const UsageBar = styled.div`
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #eeeeee;

  span {
    display: block;
    height: 100%;
    border-radius: inherit;
  }
`;

const DailyUsageSection = styled.section`
  padding-top: 14px;
  border-top: 1px solid #e7e5e1;

  h3 {
    margin: 0 0 4px;
    font-family: "Bio Sans SemiBold", Arial, sans-serif;
    font-size: clamp(12px, 3.3vw, 16px);
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #e7e5e1;
    font-size: clamp(10px, 2.9vw, 14px);

    &:last-child {
      padding-bottom: 0;
      border-bottom: 0;
    }

    span {
      color: #6e7480;
    }

    strong {
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    }
  }
`;

const InstallationGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const InstallationLead = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const QRImage = styled.img`
  width: clamp(100px, 30vw, 145px);
  height: clamp(100px, 30vw, 145px);
  flex: none;
  padding: 8px;
  border: 1px solid #e7e5e1;
  border-radius: 12px;
  object-fit: contain;
  background: #ffffff;
`;

const QRPlaceholder = styled.div`
  width: clamp(100px, 30vw, 145px);
  height: clamp(100px, 30vw, 145px);
  display: grid;
  place-items: center;
  flex: none;
  border: 1px dashed #c9c9c9;
  border-radius: 12px;
  color: #777777;
  background: #f7f7f5;
  text-align: center;
`;

const InstallNote = styled.p`
  margin: 0;
  color: #6e7480;
  font-size: clamp(11px, 3.1vw, 15px);
  line-height: 1.5;
`;

const CodeRow = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border: 1px solid #e7e5e1;
  border-radius: 9px;
  background: #f7f7f5;

  code {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CopyButton = styled.button`
  flex: none;
  padding: 7px 9px;
  border: 1px solid #d6d6d4;
  border-radius: 7px;
  background: #ffffff;
`;

const TechnicalList = styled.dl`
  display: grid;
  gap: 10px;
  margin: 0;

  div {
    padding-top: 10px;
    border-top: 1px solid #eceae7;
  }

  dt {
    color: #6e7480;
    font-size: 12px;
  }

  dd {
    margin: 4px 0 0;
    overflow-wrap: anywhere;
    font-size: 13px;
    white-space: pre-line;
  }
`;

const HelpCard = styled(Card)`
  padding: clamp(14px, 4vw, 22px);
`;

const HelpHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 12px;
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(16px, 4.5vw, 22px);
`;

const HelpGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;

const HelpLink = styled.a`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: clamp(10px, 3vw, 15px);
  border: 1px solid #e7e5e1;
  border-radius: 11px;
  color: #171717;
  background: #ffffff;
  text-align: left;
  text-decoration: none;
`;

const HelpButton = styled.button`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: clamp(10px, 3vw, 15px);
  border: 1px solid #e7e5e1;
  border-radius: 11px;
  color: #171717;
  background: #ffffff;
  text-align: left;
`;

const HelpText = styled.span`
  min-width: 0;
  flex: 1;

  strong {
    display: block;
    font-size: clamp(11px, 3.2vw, 16px);
    line-height: 1.25;
  }

  span {
    display: block;
    margin-top: 3px;
    color: #6e7480;
    font-size: clamp(9px, 2.5vw, 12px);
    line-height: 1.25;
  }
`;

const FooterNote = styled.aside`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: clamp(13px, 3.8vw, 20px);
  border: 1px solid #f0d27a;
  border-radius: 14px;
  background: #fff6d9;

  > div {
    min-width: 0;

    > strong,
    > span {
      display: block;
    }

    > strong {
      font-size: clamp(12px, 3.4vw, 17px);
    }

    > span {
      margin-top: 3px;
      color: #6e7480;
      font-size: clamp(10px, 2.8vw, 14px);
    }
  }
`;

const Toast = styled.div<{ $visible: boolean }>`
  position: fixed;
  z-index: 1200;
  bottom: 18px;
  left: 50%;
  padding: 11px 15px;
  border-radius: 9px;
  color: #ffffff;
  background: #191919;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: none;
  transform: translate(-50%, ${({ $visible }) => ($visible ? "0" : "70px")});
  transition: 0.2s ease;
`;

function extractUrlFromLpa(lpaString: string): string {
  const parts = lpaString?.split("$") ?? [];
  return parts.length >= 2 ? parts[1] : lpaString;
}

function extractActivationCode(lpaString?: string): string | null {
  const parts = lpaString?.split("$") ?? [];
  return parts.length >= 3 ? parts[2] : null;
}

function extractUssd(instruction: string): string | null {
  return instruction.match(/\*[0-9*]+#/)?.[0] ?? null;
}

function formatPlanValidity(dataPlan: string): string {
  const match = dataPlan.match(/(\d+)\s*days?/i);
  if (!match) return dataPlan || "Not available";
  const days = Number(match[1]);
  return `${days} ${days === 1 ? "day" : "days"} from activation`;
}

function formatDate(value?: string | number | null, milliseconds = false): string {
  if (value === undefined || value === null || value === "") return "Not available";
  const parsed = milliseconds ? dayjs(Number(value)) : dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : "Not available";
}

function formatGb(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return value.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
}

function mapJoytelStatus(status?: string | number | null): string {
  if (status === "0" || status === 0) return "Pending Installation";
  if (status === "1" || status === 1) return "Active";
  if (status === "2" || status === 2) return "Plan expired";
  return "Status unavailable";
}

function mapTgtStatus(status?: string | null, hasOrder = true): string {
  if (!hasOrder) return "Pending - QR Code";
  switch (status) {
    case "NOTACTIVE":
      return "Pending - QR Installation";
    case "INUSE":
    case "ACTIVATED":
      return "Active";
    case "USED":
      return "Used";
    case "EXPIRED":
      return "Plan expired";
    default:
      return status || "Loading...";
  }
}

function isActiveStatus(status: string): boolean {
  return status === "Active";
}

function Accordion(props: {
  name: PanelName;
  title: string;
  hint?: string;
  icon: ReactNode;
  open: boolean;
  onToggle: (name: PanelName) => void;
  children: ReactNode;
}) {
  const id = `${props.name}-panel`;
  return (
    <Card>
      <AccordionButton
        type="button"
        aria-expanded={props.open}
        aria-controls={id}
        onClick={() => props.onToggle(props.name)}
      >
        <AccordionIcon>{props.icon}</AccordionIcon>
        <AccordionCopy>
          <AccordionTitle>{props.title}</AccordionTitle>
          {props.hint && <AccordionHint>{props.hint}</AccordionHint>}
        </AccordionCopy>
        <Chevron $open={props.open} />
      </AccordionButton>
      <AccordionBody $open={props.open} id={id}>
        <div>
          <AccordionInner>{props.children}</AccordionInner>
        </div>
      </AccordionBody>
    </Card>
  );
}

export default function EsimUsagePage(props: { navigation?: ReactNode }) {
  const { redemptionCode, simIdx } = useParams<PageParams>();
  const parsedSimIdx = parseInt(simIdx ?? "0", 10);
  const [openPanels, setOpenPanels] = useState<Record<PanelName, boolean>>({
    plan: false,
    usage: false,
    balance: false,
    installation: false,
  });
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [toast, setToast] = useState("");
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data: redemptionData } = useGetRedemptionQuery(
    { orderNo: redemptionCode ?? "", simIdx: parsedSimIdx },
    { skip: !redemptionCode || !simIdx }
  );

  const coupon = redemptionData?.redemption?.snPin ?? "";
  const isJoytel = Boolean(redemptionData?.joytel);
  const internalSku = redemptionData?.redemption?.InternalSku ?? "";
  const isTgt = internalSku.toLowerCase().startsWith("tgt-");
  const isLiveProvider = isJoytel || isTgt;
  const hasTgtOrderDetails = Boolean(redemptionData?.redemption?.TGT_OrderDetails);

  const {
    data: joytelUsage,
    isLoading: joytelUsageLoading,
    error: joytelUsageError,
  } = useGetEsimUsageQuery({ coupon }, { skip: !coupon });
  const {
    data: joytelStatus,
    isLoading: joytelStatusLoading,
    error: joytelStatusError,
  } = useGetEsimStatusQuery(
    { coupon },
    { skip: !coupon || !isJoytel }
  );
  const {
    data: tgtUsage,
    isLoading: tgtUsageLoading,
    error: tgtUsageError,
  } = useGetTgtUsageQuery(
    { orderNo: redemptionCode ?? "", simIdx: parsedSimIdx },
    { skip: !redemptionCode || !simIdx || !hasTgtOrderDetails }
  );
  const { data: tgtOrder } = useGetTgtOrderQuery(
    { orderNo: redemptionCode ?? "", simIdx: parsedSimIdx },
    { skip: !redemptionCode || !simIdx || !hasTgtOrderDetails }
  );

  const plan = redemptionData?.mapping?.ItemDetails?.Plan;
  const planType = redemptionData?.mapping?.ItemDetails?.PlanType ?? "";
  const allowance = Number(redemptionData?.mapping?.ItemDetails?.DataAllowance ?? 0);
  const dataPlan = plan?.DataPlan ?? "";
  const validity = formatPlanValidity(dataPlan);
  const mainCountry = plan?.MainCountry || "Your plan";
  const coverage = useCOperators(plan?.CoverageCountries || "");
  const coverageCount = coverage.length;
  const instruction = redemptionData?.mapping?.RedemptionDetails?.CheckDataInstruction || "";
  const ussd = extractUssd(instruction);
  const isInstructionPlan = planType === "Local" || planType === "Others";

  const dailyUsage = useMemo(() => {
    if (!isJoytel) return [];
    const grouped = (joytelUsage?.data?.dataUsageList ?? []).reduce((totals, item) => {
      totals[item.usageDate] = (totals[item.usageDate] || 0) + (parseInt(item.usage, 10) || 0);
      return totals;
    }, {} as Record<string, number>);
    return Object.entries(grouped).sort(([a], [b]) => b.localeCompare(a));
  }, [isJoytel, joytelUsage]);

  const usageSummary = useMemo<UsageSummary | null>(() => {
    if (isJoytel && joytelUsage?.data && !isInstructionPlan && allowance > 0) {
      let usageBytes = Number(joytelUsage.data.totalUsage || 0);
      if (planType === "Daily" && joytelUsage.data.dataUsageList?.length) {
        const latestDate = [...joytelUsage.data.dataUsageList]
          .sort((a, b) => b.usageDate.localeCompare(a.usageDate))[0]?.usageDate;
        usageBytes = joytelUsage.data.dataUsageList
          .filter((item) => item.usageDate === latestDate)
          .reduce((sum, item) => sum + Number(item.usage || 0), 0);
      }
      const usedGb = usageBytes / BYTES_PER_GB;
      return {
        usedGb,
        totalGb: allowance,
        remainingGb: Math.max(allowance - usedGb, 0),
      };
    }

    const tgtSuccess = String(tgtUsage?.code) === "0000";
    if (isTgt && tgtSuccess && tgtUsage?.data && !isInstructionPlan) {
      if (planType === "Daily" && allowance > 0) {
        const usedGb = Number(tgtUsage.data.qtaconsumption ?? tgtUsage.data.dataUsage ?? 0) / MB_PER_GB;
        return {
          usedGb,
          totalGb: allowance,
          remainingGb: Math.max(allowance - usedGb, 0),
        };
      }
      const totalGb = Number(tgtUsage.data.dataTotal ?? 0) / MB_PER_GB;
      if (totalGb > 0) {
        const usedGb = Number(tgtUsage.data.dataUsage ?? 0) / MB_PER_GB;
        const remainingGb = tgtUsage.data.dataResidual !== undefined
          ? Number(tgtUsage.data.dataResidual) / MB_PER_GB
          : Math.max(totalGb - usedGb, 0);
        return { usedGb, totalGb, remainingGb };
      }
    }

    return null;
  }, [allowance, isInstructionPlan, isJoytel, isTgt, joytelUsage, planType, tgtUsage]);

  const variant: StatusVariant = isLiveProvider
    ? usageSummary
      ? "live-usage"
      : "live-status"
    : "third-party";

  const providerUsageLoading = isJoytel ? joytelUsageLoading : isTgt ? tgtUsageLoading : false;
  const providerUsageError = isJoytel ? joytelUsageError : isTgt ? tgtUsageError : null;
  const tgtOrderItem = tgtOrder?.data?.list?.[0];
  const status = isJoytel
    ? joytelStatusLoading
      ? "Loading..."
      : joytelStatusError
        ? "Status unavailable"
        : mapJoytelStatus(joytelStatus?.data?.status)
    : isTgt
      ? mapTgtStatus(tgtOrderItem?.orderStatus, hasTgtOrderDetails)
      : "";
  const validUntil = isJoytel
    ? formatDate(joytelUsage?.data?.expTime, true)
    : isTgt
      ? formatDate(
          tgtOrderItem?.activatedEndTime || redemptionData?.redemption?.TGT_OrderDetails?.activatedEndTime
        )
      : "";
  const active = isActiveStatus(status);

  const isActivated = isJoytel
    ? active
    : isTgt
      ? ["Active", "Used", "Plan expired"].includes(status)
      : redemptionData?.redemption?.IsSent === true;

  const qrUrl = isJoytel && redemptionData?.redemption?.s3_link
    ? redemptionData.redemption.s3_link
    : redemptionData?.redemption?.TGT_QRCodeUrl
      ? redemptionData.redemption.TGT_QRCodeUrl
      : isActivated
        ? redemptionData?.esim?.QrImageUrl || exampleSimplyQr
        : null;

  const installationValue = redemptionData?.redemption?.AssignedSimLdap
    ?? redemptionData?.redemption?.qrCodeValue
    ?? redemptionData?.esim?.ActivationCodeUrl
    ?? redemptionData?.esim?.ActivationCode
    ?? "";
  const joytelActivationCode = extractActivationCode(redemptionData?.redemption?.qrCodeValue);
  const smdpAddress = redemptionData?.redemption?.qrCodeValue
    ? extractUrlFromLpa(redemptionData.redemption.qrCodeValue)
    : redemptionData?.esim?.SmdpAddress || "-";
  const calls = plan?.TalkTime && !/no minutes|data only/i.test(plan.TalkTime)
    ? plan.TalkTime
    : "Data only";
  const productName = /esim/i.test(mainCountry) ? mainCountry : `${mainCountry} eSIM`;
  const coverageText = `${mainCountry}${coverageCount ? ` • ${coverageCount} countries` : ""}`;
  const allowanceText = allowance ? `${formatGb(allowance)} GB` : dataPlan || "Not available";
  const planHint = [coverageText, allowance ? `${formatGb(allowance)} GB` : "", validity]
    .filter(Boolean)
    .join(" · ");

  const technicalDetails = [
    ["Online Order Number", redemptionData?.joytel?.oNumber || redemptionData?.redemption?.OrderId],
    ["eSIM plan details", redemptionData?.title],
    ["Installation PIN", redemptionData?.esim?.ConfirmationCode],
    ["eSIM serial number", redemptionData?.redemption?.snCode || redemptionData?.redemption?.AssignedSimId],
    ["APN details", redemptionData?.mapping?.RedemptionDetails?.ApnSetting || "rsp-eu.simlessly.com"],
    ["Installation URL", installationValue || "-"],
    ["SM-DP+ address", smdpAddress],
    ["Rental contract number", redemptionData?.redemption?.TGT_OrderDetails?.cardInfo?.rentalContractNumber],
    ["Activation code", joytelActivationCode || redemptionData?.esim?.ActivationCode],
  ].filter((item): item is string[] => Boolean(item[1]));

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  function showToast(message: string) {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2400);
  }

  function togglePanel(name: PanelName) {
    setOpenPanels((current) => ({ ...current, [name]: !current[name] }));
  }

  function openBalanceInstructions() {
    setOpenPanels((current) => ({ ...current, balance: true }));
    document.getElementById("balance-panel")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  async function copyInstallationValue() {
    if (!installationValue) return;
    try {
      await navigator.clipboard.writeText(installationValue);
      showToast("Installation code copied");
    } catch {
      showToast("Unable to copy installation code");
    }
  }

  function openSupport() {
    const frame = document.getElementById("zcw-iframe") as HTMLIFrameElement | null;
    if (!frame?.contentWindow) {
      showToast("Support chat is still loading");
      return;
    }
    frame.contentWindow.postMessage({ type: "zcw:open-chat", payload: undefined }, "*");
  }

  const statusItems = (
    <>
      <StatusItem>
        <CircleIcon><CalendarOutlined /></CircleIcon>
        <div>
          <StatusLabel>Valid until</StatusLabel>
          <StatusValue>{validUntil}</StatusValue>
        </div>
      </StatusItem>
      <StatusItem>
        <CircleIcon $success={active} $warning={!active}>
          <CheckCircleFilled />
        </CircleIcon>
        <div>
          <StatusLabel>Status</StatusLabel>
          <StatusChip $active={active}>{status}</StatusChip>
        </div>
      </StatusItem>
    </>
  );

  if (!redemptionData) {
    return (
      <PageRoot>
        <Header>
          <MobileCanvas><HeaderLogo src="/logo-transparent.png" alt="SIMPLY" /></MobileCanvas>
        </Header>
        <Content><Spin /></Content>
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <Header>
        <MobileCanvas>
          <HeaderRow>
            <div>
              <HeaderLogo src="/logo-transparent.png" alt="SIMPLY" />
              <ReadyText>Your eSIM is ready 🎉</ReadyText>
            </div>
            {variant === "live-status" && <HeaderStatus>{status}</HeaderStatus>}
          </HeaderRow>
        </MobileCanvas>
      </Header>

      <Content>
        {props.navigation && <NavigationSlot>{props.navigation}</NavigationSlot>}

        <HeroCard aria-label="eSIM status">
          <ProductHeader>
            <ProductBadge>
              {redemptionData.image
                ? <img src={redemptionData.image} alt="" />
                : <GlobalOutlined />}
            </ProductBadge>
            <div>
              <ProductName>{productName}</ProductName>
              <Muted>{coverageText}</Muted>
            </div>
          </ProductHeader>

          <HeroGrid>
            {variant === "live-usage" && usageSummary ? (
              <HeroColumn aria-label="Data usage">
                <Meter $usedPercentage={Math.min((usageSummary.usedGb / usageSummary.totalGb) * 100, 100)}>
                  <MeterValue>
                    {formatGb(usageSummary.remainingGb)} GB
                    <span>left</span>
                  </MeterValue>
                </Meter>
                <UsageCaption>
                  <strong>{formatGb(usageSummary.usedGb)} GB</strong> used<br />
                  of {formatGb(usageSummary.totalGb)} GB
                </UsageCaption>
              </HeroColumn>
            ) : variant === "live-status" ? (
              <HeroColumn>
                <HeroIcon><MobileOutlined /></HeroIcon>
                <HeroTitle>Data usage</HeroTitle>
                {providerUsageLoading ? (
                  <Spin size="small" />
                ) : (
                  <InstructionText>
                    {providerUsageError
                      ? "Failed to load live usage data. Check your remaining data with the provider."
                      : "Live usage is not available for this eSIM. Check your remaining data with the provider."}
                  </InstructionText>
                )}
                {ussd && <ShortCode><PhoneOutlined />&nbsp; Dial {ussd}</ShortCode>}
                {ussd ? (
                  <ActionLink href={`tel:${ussd}`}>
                    <span className="button-icon" aria-hidden="true" />
                    <span className="button-label">Check data balance</span>
                    <ArrowRightOutlined className="arrow" />
                  </ActionLink>
                ) : instruction ? (
                  <ActionButton type="button" onClick={openBalanceInstructions}>
                    <span className="button-icon" aria-hidden="true" />
                    <span className="button-label">Check data balance</span>
                    <ArrowRightOutlined className="arrow" />
                  </ActionButton>
                ) : null}
              </HeroColumn>
            ) : (
              <HeroColumn>
                <HeroIcon><PhoneOutlined /></HeroIcon>
                <HeroTitle>How to check your data</HeroTitle>
                <ShortCode>{ussd ? `Dial ${ussd}` : instruction || "Check with your provider"}</ShortCode>
              </HeroColumn>
            )}

            <RightColumn>
              {variant === "third-party" ? (
                <>
                  <GenericValidity>
                    <HeroIcon><CalendarOutlined /></HeroIcon>
                    <HeroTitle>Your plan validity</HeroTitle>
                    <StatusValue>{validity}</StatusValue>
                    <p>Live updates aren’t available for this eSIM, but you can check your data directly with the provider.</p>
                  </GenericValidity>
                  <ActionLink $outline href={INSTALLATION_GUIDE_URL} target="_blank" rel="noreferrer">
                    <BookOutlined className="button-icon" />
                    <span className="button-label">Installation guide</span>
                    <ArrowRightOutlined className="arrow" />
                  </ActionLink>
                </>
              ) : (
                <>
                  {statusItems}
                  <ActionLink href={BUY_MORE_DATA_URL} target="_blank" rel="noreferrer">
                    <span className="button-icon" aria-hidden="true" />
                    <span className="button-label">Buy more data</span>
                    <ArrowRightOutlined className="arrow" />
                  </ActionLink>
                  <ActionLink $outline href={INSTALLATION_GUIDE_URL} target="_blank" rel="noreferrer">
                    <BookOutlined className="button-icon" />
                    <span className="button-label">Installation guide</span>
                    <ArrowRightOutlined className="arrow" />
                  </ActionLink>
                </>
              )}
            </RightColumn>
          </HeroGrid>
        </HeroCard>

        <Stack>
          <Accordion
            name="plan"
            title="Plan details"
            hint={planHint}
            icon={<SimCardIcon />}
            open={openPanels.plan}
            onToggle={togglePanel}
          >
            <DetailGrid>
              <DetailBox><span>Data allowance</span><strong>{allowanceText}</strong></DetailBox>
              <DetailBox><span>Plan validity</span><strong>{validity}</strong></DetailBox>
              <DetailBox><span>Calls & SMS</span><strong>{calls}</strong></DetailBox>
            </DetailGrid>
          </Accordion>

          {variant === "live-usage" && usageSummary && (
            <Accordion
              name="usage"
              title="Usage breakdown"
              hint="See your latest data use"
              icon={<UsageBreakdownIcon />}
              open={openPanels.usage}
              onToggle={togglePanel}
            >
              <UsageList>
                <UsageRow>
                  <span>High-speed data</span>
                  <UsageBar><span style={{ width: `${Math.min((usageSummary.usedGb / usageSummary.totalGb) * 100, 100)}%`, background: "#ff4b31" }} /></UsageBar>
                  <strong>{formatGb(usageSummary.usedGb)} / {formatGb(usageSummary.totalGb)} GB</strong>
                </UsageRow>
                <UsageRow>
                  <span>Remaining</span>
                  <UsageBar><span style={{ width: `${Math.min((usageSummary.remainingGb / usageSummary.totalGb) * 100, 100)}%`, background: "#36b54a" }} /></UsageBar>
                  <strong>{formatGb(usageSummary.remainingGb)} GB</strong>
                </UsageRow>
                {dailyUsage.length > 0 && (
                  <DailyUsageSection>
                    <h3>Daily usage</h3>
                    <ul aria-label="Daily usage">
                      {dailyUsage.map(([date, usageBytes]) => (
                        <li key={date}>
                          <span>{formatDate(date)}</span>
                          <strong>{(usageBytes / BYTES_PER_GB).toFixed(2)} GB</strong>
                        </li>
                      ))}
                    </ul>
                  </DailyUsageSection>
                )}
                <InstructionText>Usage is updated by the provider. It may take a few minutes to refresh.</InstructionText>
              </UsageList>
            </Accordion>
          )}

          {variant === "live-status" && instruction && (
            <Accordion
              name="balance"
              title="How to check your balance"
              icon={<MobileOutlined />}
              open={openPanels.balance}
              onToggle={togglePanel}
            >
              <InstructionText>{instruction}</InstructionText>
            </Accordion>
          )}

          <Card>
            <AccordionButton type="button" aria-haspopup="dialog" onClick={() => setCoverageOpen(true)}>
              <AccordionIcon><SignalFilled /></AccordionIcon>
              <AccordionCopy>
                <AccordionTitle>Coverage &amp; network</AccordionTitle>
                <AccordionHint>{coverageCount ? `${coverageCount} countries and supported operators` : "Supported countries and operators"}</AccordionHint>
              </AccordionCopy>
              <DownOutlined />
            </AccordionButton>
          </Card>

          <Accordion
            name="installation"
            title="View eSIM installation details"
            hint="QR code and activation code"
            icon={<LockOutlined />}
            open={openPanels.installation}
            onToggle={togglePanel}
          >
            <InstallationGrid>
              <InstallationLead>
                {qrUrl
                  ? <QRImage src={qrUrl} alt="SIMPLY eSIM QR code" />
                  : <QRPlaceholder>QR code unavailable</QRPlaceholder>}
                <InstallNote>
                  Install on the device you will use during your trip. Some eSIMs can only be installed once.
                </InstallNote>
              </InstallationLead>
              {installationValue && (
                <CodeRow>
                  <code>{installationValue}</code>
                  <CopyButton type="button" onClick={copyInstallationValue} aria-label="Copy installation code">
                    <CopyOutlined />
                  </CopyButton>
                </CodeRow>
              )}
              <TechnicalList>
                {technicalDetails.map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </TechnicalList>
            </InstallationGrid>
          </Accordion>

          <HelpCard>
            <HelpHeading><HelpHeadingIcon $warning><LifeBuoyIcon /></HelpHeadingIcon> Need help?</HelpHeading>
            <HelpGrid>
              <HelpLink href={INSTALLATION_GUIDE_URL} target="_blank" rel="noreferrer">
                <CircleIcon $warning><ToolOutlined /></CircleIcon>
                <HelpText><strong>eSIM troubleshooting</strong><span>Fix connection or setup issues</span></HelpText>
                <ArrowRightOutlined />
              </HelpLink>
              <HelpButton type="button" onClick={openSupport}>
                <CircleIcon $warning><CustomerServiceOutlined /></CircleIcon>
                <HelpText><strong>Contact support</strong><span>We’re here to help</span></HelpText>
                <ArrowRightOutlined />
              </HelpButton>
            </HelpGrid>
          </HelpCard>
        </Stack>

        <FooterNote>
          <SavedPageIcon><SecureShieldIcon /></SavedPageIcon>
          <div><strong>Keep this page saved for easy access</strong><span>Useful for setup and troubleshooting.</span></div>
        </FooterNote>
      </Content>

      <CountryListModal countries={coverage} isOpen={coverageOpen} setIsOpen={setCoverageOpen} />
      <Toast $visible={Boolean(toast)} role="status" aria-live="polite">{toast}</Toast>
    </PageRoot>
  );
}
